import { AfterViewInit, Component, DestroyRef, inject, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal, NgbModalOptions, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { WorkOrderPaginationRequest } from '../../models/work-order.models';
import { WorkOrder, WorkOrderItem } from '../../models/work-orders-overview.models';
import { WorkOrderPriority, WorkOrderPriorityLabels, WorkOrderStatus, WorkOrderStatusLabels, WorkOrderSourceLabels, WorkOrderServiceTypesLabels, WORK_ORDER_OVERDUE_STATUS_LABEL } from '../../enums/work-order.enum';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { TotalWorkOrdersWidgetComponent } from '../widgets/total-work-orders-widget/total-work-orders-widget.component';
import { InProgressWidgetComponent } from '../widgets/in-progress-widget/in-progress-widget.component';
import { OverdueWidgetComponent } from '../widgets/overdue-widget/overdue-widget.component';
import { AvgResolutionWidgetComponent } from '../widgets/avg-resolution-widget/avg-resolution-widget.component';
import { DateFormatPipe } from '../../../../../_shared/pipes/date-formatter.pipe';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { ConfirmationModalComponent } from '../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationOptions } from '../../../../../_shared/models/modals/confirmation-options.model';
import { WorkOrdersOverviewService } from '../../services/work-orders-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { AccountFilterData, WidgetFilterData, WorkshopFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { EditWorkOrderModalComponent } from '../modals/edit-work-order-modal/edit-work-order-modal.component';
import { ViewDetailsModalComponent } from '../modals/view-details-modal/view-details-modal.component';
import { ScheduleAndAssignModalComponent } from '../modals/schedule-and-assign-modal/schedule-and-assign-modal.component';
import { PhotosVideosViewerModalComponent } from '../../../../_shared/components/modals/photos-videos-viewer-modal/photos-videos-viewer-modal.component';
import { ToastrService } from 'ngx-toastr';
import { SortDirection, WorkOrderSortColumn } from '../../types/types';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { WorkshopFilterComponent } from '../../../../_shared/components/filters/workshop-filter/workshop-filter.component';
import { MakeModelFilterData } from '../../../../_shared/models/make-model.model';
import { MakeAndModelFilterComponent } from "../../../../_shared/components/filters/make-model-filter/make-model-filter.component";
import { FilterService } from '../../../../../_shared/services/filter.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-work-orders-overview',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    NgbTooltipModule,
    EmptyStateComponent,
    ErrorStateComponent,
    TotalWorkOrdersWidgetComponent,
    InProgressWidgetComponent,
    OverdueWidgetComponent,
    AvgResolutionWidgetComponent,
    PaginationComponent,
    SearchComponent,
    WorkshopFilterComponent,
    MakeAndModelFilterComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './work-orders-overview.component.html',
  styleUrl: './work-orders-overview.component.scss'
})
export class WorkOrdersOverviewComponent implements AfterViewInit, OnDestroy {
  readonly OVERDUE_STATUS = 'overdue';

  private destroyRef = inject(DestroyRef);
  private modalService = inject(NgbModal);
  private workOrdersService = inject(WorkOrdersOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private filterService = inject(FilterService);
  private transloco = inject(TranslocoService);

  @Input() currencySymbol: string = 'R';

  accountFilter: AccountFilterData = {} as AccountFilterData;
  accountIdsChanged$: Subject<number[]> = new Subject();

  // Internal data state
  workOrders: WorkOrderItem[] = [];
  isLoading: boolean = true;
  hasError: boolean = false;
  page: number = 1;
  readonly pageSize: number = 5;
  totalPages: number = 0;
  totalCount: number = 0;

  // Internal filter state
  searchTerm: string = '';
  workshopFilter: WorkshopFilterData = { workshops: [] };
  makeModelFilter: MakeModelFilterData = { makeModels: [] };
  statusFilter: WorkOrderStatus = WorkOrderStatus.All;
  sortColumn: WorkOrderSortColumn | null = null;
  sortDirection: SortDirection = null;

  isExporting: boolean = false;

  private readonly cancelSubject = new Subject<void>();

  // Enums for template
  WorkOrderPriority = WorkOrderPriority;
  WorkOrderStatus = WorkOrderStatus;
  WorkOrderPriorityLabels = WorkOrderPriorityLabels;
  WorkOrderStatusLabels = WorkOrderStatusLabels;
  WorkOrderSourceLabels = WorkOrderSourceLabels;
  WorkOrderServiceTypeLabels = WorkOrderServiceTypesLabels;

  private dateFormatPipe = new DateFormatPipe();

  get showingCount(): number {
    if (this.totalCount === 0) return 0;
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  ngAfterViewInit(): void {
    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filter) => {
        const accountIds = filter.accountIds;
        this.accountFilter = { accountIds };
        this.accountIdsChanged$.next(accountIds);

        this.page = 1;
        this.makeModelFilter = { makeModels: [] };
        this.workshopFilter = { workshops: [] };
        this.statusFilter = WorkOrderStatus.All;
        this.loadData();
      });
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const workshopIds = this.workshopFilter.workshops
        ?.map(w => w.id)
        .filter(id => id > 0) ?? [];

      const vehicleModelIds = this.makeModelFilter.makeModels
        ?.map(m => m.modelId)
        .filter(id => id > 0) ?? [];

      const request: WorkOrderPaginationRequest = {
        accountIds: this.accountFilter.accountIds ?? [],
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm,
        sortColumn: this.sortColumn ?? undefined,
        sortDirection: this.sortDirection ?? undefined,
        statuses: this.statusFilter === WorkOrderStatus.All
          ? [WorkOrderStatus.Open, WorkOrderStatus.InProgress, WorkOrderStatus.Completed]
          : [this.statusFilter],
        workshopIds: workshopIds.length > 0 ? workshopIds : undefined,
        vehicleModelIds: vehicleModelIds.length > 0 ? vehicleModelIds : undefined,
      };

      this.cancelSubject.next();
      const response = await this.workOrdersService.getWorkOrdersAsync(request, this.cancelSubject);

      this.totalPages = response.data?.totalPages ?? 0;
      this.totalCount = response.data?.totalCount ?? 0;
      this.workOrders = response.data?.results ?? [];

      if (this.workOrders.length === 0 && this.page > 1) {
        this.page = 1;
        await this.loadData();
        return;
      }

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private refreshKpiWidgets(): void {
    if (this.accountFilter.accountIds?.length > 0) {
      this.filterService.updateFilter({ accountIds: this.accountFilter.accountIds } as WidgetFilterData);
    }
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.loadData();
  }

  onWorkshopFilterChange(filter: WorkshopFilterData): void {
    this.workshopFilter = filter;
    this.page = 1;
    this.loadData();
  }

  onMakeAndModelChange(filter: MakeModelFilterData): void {
    this.makeModelFilter = filter;
    this.page = 1;
    this.loadData();
  }

  onStatusFilterChange(status: WorkOrderStatus): void {
    this.statusFilter = status;
    this.page = 1;
    this.loadData();
  }

  formatCost(cost: number): string {
    return `${this.currencySymbol}${cost.toLocaleString()}`;
  }

  formatDate(date: Date | string): string {
    return this.dateFormatPipe.transform(date);
  }

  getDisplayStatus(workOrder: Pick<WorkOrderItem, 'status' | 'scheduledDate'>): WorkOrderStatus | typeof this.OVERDUE_STATUS {
    if (workOrder.status === WorkOrderStatus.Open &&
      this.hasScheduledDatePassed(workOrder.scheduledDate)) {
      return this.OVERDUE_STATUS;
    }

    return workOrder.status;
  }

  getDisplayStatusLabel(status: WorkOrderStatus | typeof this.OVERDUE_STATUS): string {
    return status === this.OVERDUE_STATUS ? WORK_ORDER_OVERDUE_STATUS_LABEL : WorkOrderStatusLabels[status];
  }

  private hasScheduledDatePassed(scheduledDate: Date | string | null): boolean {
    if (!scheduledDate) {
      return false;
    }

    const date = new Date(scheduledDate);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    return date <= new Date();
  }

  getPriorityClass(priority: WorkOrderPriority): string {
    switch (priority) {
      case WorkOrderPriority.Low:
        return 'priority-low';
      case WorkOrderPriority.Medium:
        return 'priority-medium';
      case WorkOrderPriority.High:
        return 'priority-high';
      default:
        return '';
    }
  }

  getWorkOrderStatusClass(status: WorkOrderStatus | typeof this.OVERDUE_STATUS): string {
    switch (status) {
      case WorkOrderStatus.Pending:
        return 'status-overdue';
      case WorkOrderStatus.Open:
        return 'status-open';
      case WorkOrderStatus.InProgress:
        return 'status-in-progress';
      case WorkOrderStatus.Completed:
        return 'status-completed';
      case WorkOrderStatus.Rejected:
        return 'status-overdue';
      case this.OVERDUE_STATUS:
        return 'status-overdue';
      default:
        return '';
    }
  }

  getPhotoCountLabel(workOrder: WorkOrderItem): string {
    const count = workOrder.faultImages?.length ?? 0;
    const noun = this.transloco.translate(count === 1 ? 'workOrder.table.photo' : 'workOrder.table.photos');
    return `${count} ${noun}`;
  }

  onViewPhotos(workOrder: WorkOrderItem): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      windowClass: 'photos-videos-viewer-modal'
    };

    const modalRef = this.modalService.open(PhotosVideosViewerModalComponent, modalOptions);
    modalRef.componentInstance.title = this.transloco.translate('workOrder.photos.title');
    modalRef.componentInstance.contextLabel = workOrder.workOrderNumber;
    modalRef.componentInstance.items = workOrder.faultImages ?? [];
    modalRef.componentInstance.downloadLinkResolver = async (item: { reference: string }): Promise<string | null> => {
      try {
        const response = await this.workOrdersService.getWorkOrderFaultImageDownloadLinkAsync(
          workOrder.workOrderNumber, item.reference, this.cancelSubject);
        return response.data ?? null;
      } catch {
        return null;
      }
    };
  }

  onViewDetails(workOrder: WorkOrder | any): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'view-details-modal'
    };

    const modalRef = this.modalService.open(ViewDetailsModalComponent, modalOptions);
    modalRef.componentInstance.workOrderId = Number(workOrder.workOrderId);

    modalRef.result.then(
      (result) => {
        if (result) {
          this.loadData();
          this.refreshKpiWidgets();
        }
      },
      () => { }
    );
  }

  onEdit(workOrder: WorkOrderItem | any): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'edit-work-order-modal'
    };

    const modalRef = this.modalService.open(EditWorkOrderModalComponent, modalOptions);
    modalRef.componentInstance.workOrderId = workOrder.workOrderId;

    modalRef.result.then(
      (result) => {
        if (result) {
          this.loadData();
          this.refreshKpiWidgets();
        }
      },
      () => { }
    );
  }

  onScheduleAssign(workOrder: WorkOrder | any): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'schedule-and-assign-modal'
    };

    const modalRef = this.modalService.open(ScheduleAndAssignModalComponent, modalOptions);
    modalRef.componentInstance.workOrderData = workOrder;
    modalRef.componentInstance.workOrderId = workOrder.workOrderId ?? workOrder.id;

    modalRef.result.then(
      (result) => {
        if (result) {
          this.loadData();
          this.refreshKpiWidgets();
        }
      },
      () => { }
    );
  }

  onDelete(workOrder: WorkOrder | any): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(ConfirmationModalComponent, modalOptions);
    modalRef.componentInstance.data = {
      title: this.transloco.translate('workOrder.confirmations.delete.title'),
      message: this.transloco.translate('workOrder.confirmations.delete.message', { workOrderNumber: workOrder.workOrderNumber }),
      confirmText: this.transloco.translate('workOrder.confirmations.delete.confirmText'),
      declineText: this.transloco.translate('workOrder.confirmations.delete.declineText'),
      warningMessage: this.transloco.translate('workOrder.confirmations.delete.warningMessage'),
      confirmationPrompt: 'delete',
      confirmPlaceholder: this.transloco.translate('workOrder.confirmations.delete.confirmPlaceholder'),
      onConfirm: async () => {
        try {
          await this.workOrdersService.deleteWorkOrderAsync(workOrder.workOrderId, this.cancelSubject);
          this.toastr.success(this.transloco.translate('workOrder.toasts.deleted'));
          this.loadData();
          this.refreshKpiWidgets();
        } catch (error: any) {
          this.errorHandlerService.handleError(error);
        }
      },
      closeOnSuccess: true
    } as ConfirmationOptions;
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadData();
  }

  onSort(column: WorkOrderSortColumn): void {
    const nextDirection =
      this.sortColumn !== column || !this.sortDirection
        ? 'asc'
        : this.sortDirection === 'asc'
          ? 'desc'
          : null;

    this.sortColumn = nextDirection ? column : null;
    this.sortDirection = nextDirection;
    this.page = 1;
    this.loadData();
  }

  async onExport(format: 'excel' | 'csv'): Promise<void> {
    try {
      this.isExporting = true;
      const request: WorkOrderPaginationRequest = {
        accountIds: this.accountFilter.accountIds,
        page: 1,
        pageSize: this.totalCount
      };

      const blob = await this.workOrdersService.exportWorkOrdersAsync(request, this.cancelSubject);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `WorkOrders_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      this.toastr.success(this.transloco.translate('workOrder.toasts.exportCompleted'));
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isExporting = false;
    }
  }
}